import React, { memo, useCallback, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import { Flex, Box } from '@chakra-ui/layout';
import { Modal, ModalContent, ModalOverlay } from '@chakra-ui/modal';
import { Button, MaskInput, Unform, CustomIcon } from 'components';
import { generateReport } from 'services/budgetControl';
import { queryToObj } from 'utils/query';
import { reportsGenerationValidationSchema } from 'yupSchemas/budgeControl';
import { Container, Header } from './styles';
import { openNotification } from 'utils/notification';

interface IFormData {
  periodoInicial: string;
  periodoFinal: string;
}

interface IProps {
  open: boolean;
  onClose(): void;
  history: RouteComponentProps['history'];
}

const ReportsGenerationModalComp: React.FC<IProps> = ({ open, onClose, history }) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = useCallback(async (data: IFormData) => {
    const monthInitialPeriod = Number(data.periodoInicial.slice(0, 2));
    const monthLastPeriod = Number(data.periodoFinal.slice(0, 2));

    const arrDataPeriodoInitial = data.periodoInicial.split('/');
    const arrDataPeriodoFinal = data.periodoFinal.split('/');

    const [monthInitial, dayInitial, yearInitial] = [
      arrDataPeriodoInitial[0],
      '01',
      ...arrDataPeriodoInitial.slice(1),
    ];
    const [monthFinal, dayFinal, yearFinal] = [
      arrDataPeriodoFinal[0],
      '01',
      ...arrDataPeriodoFinal.slice(1),
    ];

    const periodoInitial = new Date(`${monthInitial}/${dayInitial}/${yearInitial}`);
    const periodoFinal = new Date(`${monthFinal}/${dayFinal}/${yearFinal}`);

    const isInitialPeriodGreaterThanTheCurrentDate = periodoInitial > new Date();
    if (
      data.periodoInicial.slice(0, 2) === '00' ||
      data.periodoFinal.slice(0, 2) === '00' ||
      data.periodoInicial.slice(3) === '0000 ' ||
      data.periodoFinal.slice(3) === '0000'
    ) {
      openNotification('Período inicial ou final inválido.', 'error');
      return;
    } else if (periodoFinal < periodoInitial) {
      openNotification('O período final não pode ser inferior ao período inicial.', 'error');
      return;
    } else if (isInitialPeriodGreaterThanTheCurrentDate) {
      openNotification('O período inicial não deve ser maior que a data atual.', 'error');
      return;
    } else if (monthInitialPeriod > 12) {
      openNotification('Mês do período inicial está inválido!', 'error');
      return;
    } else if (monthLastPeriod > 12) {
      openNotification('Mês do período final está inválido!', 'error');
      return;
    }
    try {
      setIsLoading(true);

      const searchData = queryToObj(history.location.search);

      const { data: res, headers } = await generateReport({
        periodoInicial: data.periodoInicial,
        periodoFinal: data.periodoFinal,
        idCliente: searchData.idCliente ? parseInt(searchData.idCliente as string) : undefined,
      });
      const a = document.createElement('a');
      document.body.appendChild(a);
      a.target = '_blank';
      a.href = URL.createObjectURL(res);
      a.download = (headers.get('Content-Disposition') || 'relatorio.xlsx').replace(
        /^.+filename=/g,
        '',
      );
      a.click();
      a.onclick = () => document.body.removeChild(a);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return (
    <Modal isOpen={open} onClose={onClose}>
      <ModalOverlay />

      <ModalContent>
        <Container>
          <Header>
            <h1>Exportar Relatório</h1>

            <Box
              bg="brand.1300"
              borderRadius="50%"
              fontSize="21px"
              fontWeight="bold"
              display="flex"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              as="button"
              type="button"
              border="none"
              color="brand.gray300"
              onClick={onClose}
            >
              <CustomIcon size="22px" $noSphere icon="round_close" />
            </Box>
          </Header>
          <Flex>
            <Unform
              validationSchema={reportsGenerationValidationSchema}
              onSubmit={handleSubmit}
              initialData={{
                formato: 'pdf',
              }}
            >
              {/* <Label>Período</Label> */}
              <Flex gridGap="16px" mt="10px" mb="10px">
                <MaskInput
                  name="periodoInicial"
                  leftIcon="calendar"
                  mask="period"
                  label="Período inicial"
                  placeholder="Período inicial"
                  parseDate
                />
                <MaskInput
                  name="periodoFinal"
                  leftIcon="calendar"
                  mask="period"
                  label="Período final"
                  placeholder="Período final"
                  parseDate
                />
              </Flex>
              <Flex>
                <Flex justify={'space-between'} width="100%">
                  <Button
                    type="button"
                    variant="cancel"
                    style={{ border: '2px solid #737d86', color: '#737d86' }}
                    outline
                    onClick={e => {
                      e.preventDefault();
                      onClose();
                    }}
                  >
                    Cancelar
                  </Button>

                  <Button
                    variant="primary"
                    // outline
                    leftIcon="fileDonwload"
                    type="submit"
                    isLoading={isLoading}
                  >
                    Baixar
                  </Button>
                </Flex>
              </Flex>
            </Unform>
          </Flex>
        </Container>
      </ModalContent>
    </Modal>
  );
};

export const ReportsGenerationModal = memo(ReportsGenerationModalComp);
